import React, { useContext, useMemo, useState } from "react";
import { useHistory, generatePath } from 'react-router-dom';
import { useAmplitude } from 'react-amplitude-hooks';
import { DefaultCard } from 'Components/shared/cards';
import InfoModal from 'Components/shared/Modal/InfoModal';
import {
  BankTransferSymbol,
  BitcoinSymbol,
  EthereumSymbol,
  ForkliftSymbol,
  Erc20Symbol,
} from 'Components/shared/symbols';
import { REDEMPTION_METHOD } from 'Enums';
import { getTranslation } from 'Helpers/translations';
import { i18nextKeys } from 'Lang/i18nextKeys';
import i18nextTranslateDynamically from 'Lang/i18nextTranslateDynamically';
import { ConfigContext } from 'States/config/configState';
import { EnvContext } from 'States/env/envState';
import { LangContext } from 'States/lang/langState';
import { UiContext } from 'States/ui/uiState';
import { ROUTES } from 'Router/Routes';
import RedeemMethodSelectionButtonGroup from './RedeemMethodSelectionButtonGroup';

const RedemptionMethodCard = ({ redemptionMethod, defaultLanguageCode }) => {
  const {
    breakpoints: { md }
  } = useContext(UiContext);
  const { env } = useContext(EnvContext);
  const history = useHistory();
  const { logEvent } = useAmplitude();
  const { lang } = useContext(LangContext);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const {
    config: { kycTierFunctionalRestrictions },
  } = useContext(ConfigContext);

  const requiredTier = Math.max(
    kycTierFunctionalRestrictions?.availability?.redemption || 0,
    redemptionMethod.KycTier || 0
  );

  const icons = {
    [REDEMPTION_METHOD.Bank]: BankTransferSymbol,
    [REDEMPTION_METHOD.Bitcoin]: BitcoinSymbol,
    [REDEMPTION_METHOD.Ethereum]: EthereumSymbol,
    [REDEMPTION_METHOD.ERC20]: Erc20Symbol,
    [REDEMPTION_METHOD.InternalNetwork]: EthereumSymbol,
    [REDEMPTION_METHOD.Physical]: ForkliftSymbol
  };

  const Icon = icons[redemptionMethod.Type];

  const translatedAssetName = useMemo(() => {
    if (redemptionMethod.UserPays.initialTranslation.LanguageCode === lang) {
      return redemptionMethod.UserPays.initialTranslation.Name;
    }
    return getTranslation("Name", lang, defaultLanguageCode, redemptionMethod.UserPays.Translations) ||
      redemptionMethod.UserPays.initialTranslation.Name
  }, [lang, defaultLanguageCode]);

  const methodTranslations = useMemo(
    () => getTranslation("Name", lang, defaultLanguageCode, redemptionMethod.Translations, true),
    [lang, defaultLanguageCode]
  );

  const openModal = () => {
    setShowInfoModal(true);
    logEvent("Redemption details checked", {
      "redemption method type": redemptionMethod.Type
    });
  };

  const goToForm = () => history.push(
    generatePath(ROUTES.redemptionForm, { id: redemptionMethod.Id })
  );

  return (
    <>
      <DefaultCard
        dataQa={`redeem-method-${redemptionMethod.Id}`}
        name={methodTranslations.Name}
        description={i18nextTranslateDynamically(
          i18nextKeys.redeemMethodSelectionPayWith,
          { userPaysName: translatedAssetName }
        )}
        iconUrl={
          redemptionMethod.Icon && `${env.BlobUrl}/${redemptionMethod.Icon}`
        }
        svg={!redemptionMethod.Icon && <Icon size={md ? '100px' : '50px'} />}
        buttonGroup={() =>
          RedeemMethodSelectionButtonGroup({
            kycTier: requiredTier,
            openModal,
            goToForm
          })
        }
      />
      {showInfoModal && (
        <InfoModal
          show={showInfoModal}
          close={() => setShowInfoModal(false)}
          content={{
            title: methodTranslations.Name,
            description: methodTranslations.Description,
            icon: redemptionMethod.Icon
            ? `${env.BlobUrl}/${redemptionMethod.Icon}`
            : <Icon size="60%" />
          }}
          actionButton={{
            onClick: goToForm
          }}
          dataQa="redemption-method-infoModal"
        />
      )}
    </>
  );
};

export default RedemptionMethodCard;
